<template>
  <u-modal v-model="internalValue">
    <dynamic-form
      v-if="form"
      v-model="form.state"
      :input="form.input"
      :schema="form.schema as any"
      class="p-4"
      @submit="onSubmit"
    >
      <div class="flex gap-4 pt-8">
        <u-button color="white" @click="internalValue = false">
          Close
        </u-button>
        <u-button
          type="submit"
          :loading="loading"
          :disabled="
            !form?.isValid ||
            (cartEntry && useDeepEqual(form.state, cartEntry.info_submitted)) ||
            loading
          "
        >
          {{ cartEntry ? 'Save changes' : 'Add to cart' }}
        </u-button>
      </div>
    </dynamic-form>
  </u-modal>
</template>

<script lang="ts" setup>
import useForm, { type SchemaUiContentful } from '@tarcltd/form-vue'

const props = withDefaults(
  defineProps<{
    /**
     * The status of the modal.
     */
    modelValue: boolean
    /**
     * The subuser to add the entry for.
     */
    subuserId: string
    /**
     * The subuser object.
     */
    subuser?: PortalSubuser
    /**
     * The show object event.
     */
    event: PortalEvent
    /**
     * The show object show.
     */
    show: PortalShow
    /**
     * The show exhibitor.
     */
    exhibitor?: PortalExhibitor
    /**
     * Any previous show objects for this show and subuser.
     */
    showObjects?: PortalShowObject[]
    /**
     * Any cart show objects for this show and subuser.
     */
    cartShowObjects?: PortalCartShowObject[]
    /**
     * The cart entry to edit.
     */
    cartEntry?: PortalCartShowObject
    /**
     * The app loading state.
     */
    loading?: boolean
  }>(),
  {
    subuser: undefined,
    exhibitor: undefined,
    showObjects: () => [],
    cartShowObjects: () => [],
    cartEntry: undefined,
    loading: false,
  },
)
const emit = defineEmits(['update:modelValue', 'addToCart'])
const form = ref(useForm(props.show.manifest))
const internalValue = computed({
  get: () => props.modelValue,
  set(value) {
    emit('update:modelValue', value)
  },
})

function onSubmit() {
  // We only want to save the Subuser Metadata that is included in
  // the Additional Questions (Exhibitor Info) from the Event Organizer.
  const eventInfoSchema = props.event?.metadata?.info?.properties || {}
  const subuserInfo = props.subuser?.metadata?.info || {}
  const filteredExhibitorInfo: Record<string, unknown> = {}
  for (const key of Object.keys(subuserInfo)) {
    if (key in eventInfoSchema) {
      filteredExhibitorInfo[key] = (subuserInfo as Record<string, unknown>)[key]
    }
  }

  emit('addToCart', {
    exhibitor: props.exhibitor,
    old: props.cartEntry,
    new: {
      id: props.cartEntry?.id ?? useV4(),
      event_id: props.event.id,
      show_id: props.show.id,
      subuser_id: props.subuserId,
      type: 'show_object',
      basis: -1,
      division: (props.show.manifest.properties._title as SchemaUiContentful)
        ?.content,
      exhibitor_info_submitted: filteredExhibitorInfo,
      info_submitted: form.value?.state ?? {},
    },
  } as {
    exhibitor: PortalExhibitor
    old?: PortalCartShowObject
    new: PortalCartShowObject
  })

  internalValue.value = false
}

watch(
  [() => props.modelValue, () => props.show.manifest],
  ([_, value]) => {
    if (value) {
      form.value = useForm(value)
    } else {
      form.value = useForm({
        type: 'object',
        properties: {},
        required: [],
      })
    }
  },
  { deep: true },
)

watch(
  [() => form.value?.input, () => props.cartEntry],
  ([_, updatedCartEntry]) => {
    if (!form.value) {
      return
    }

    if (updatedCartEntry) {
      for (const [key, previousValue] of Object.entries(
        updatedCartEntry.info_submitted,
      )) {
        form.value.state[key] = previousValue
      }
    }
  },
  { immediate: true, deep: true },
)
</script>
